import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Download, RefreshCcw, ExternalLink, FileText, Eye, Loader2, Printer, RotateCw, Search } from "lucide-react";
import { Input } from "./ui/input";

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return d;
  }
}

function isExpired(dateStr) {
  return new Date(dateStr) < new Date();
}

export default function PermitsList({ permits, loading, onReload, isAdmin }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [renewingId, setRenewingId] = useState(null);
  const [search, setSearch] = useState("");

  const q = search.trim().toLowerCase();
  const filtered = q
    ? permits.filter(
        (p) =>
          (p.serie || "").toLowerCase().includes(q) ||
          (p.folio_short || "").toLowerCase().includes(q) ||
          (p.owner_name || "").toLowerCase().includes(q)
      )
    : permits;

  const openPrintView = (folioShort) => {
    // Navigate to printable view in same tab
    navigate(`/permits/${folioShort}/print`);
  };

  const renewPermit = async (permit) => {
    if (renewingId) return;
    setRenewingId(permit.id);
    try {
      const res = await axios.post(
        `${API}/permits/${permit.folio_short}/renew`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const nuevo = res.data;
      toast.success(`Permiso renovado: ${nuevo.folio_short} (vence ${formatDate(nuevo.expires_at)})`);
      // Open the freshly renewed PDF in a new tab, ready to print
      const pdfUrl = `${API}/permits/${nuevo.folio_short}/pdf?token=${encodeURIComponent(token)}&inline=1`;
      window.open(pdfUrl, "_blank");
      onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo renovar el permiso";
      toast.error(msg);
    } finally {
      setRenewingId(null);
    }
  };

  const downloadPdf = (folioShort) => {
    // Use hidden iframe to trigger download (works inside nested iframes)
    const url = `${API}/permits/${folioShort}/pdf?token=${encodeURIComponent(token)}`;
    let iframe = document.getElementById("__pdf_dl_frame");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "__pdf_dl_frame";
      iframe.style.display = "none";
      document.body.appendChild(iframe);
    }
    iframe.src = url;
    toast.success(`Descargando PDF ${folioShort}...`);
  };

  const openValidation = (folioShort, qrToken) => {
    window.location.href = `/vd-qr/${folioShort}/${qrToken}`;
  };

  return (
    <Card>
      <CardHeader style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <CardTitle style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FileText size={18} color="#f58238" /> Permisos emitidos
          </CardTitle>
          <CardDescription>{isAdmin ? "Mostrando todos los permisos del sistema" : "Mostrando solo tus permisos"}</CardDescription>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative" }}>
            <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" }} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por serie, folio o propietario..."
              data-testid="permits-search-input"
              style={{ paddingLeft: 32, width: 280, height: 36 }}
            />
          </div>
          <Button variant="outline" size="sm" onClick={onReload} disabled={loading}>
            <RefreshCcw size={14} style={{ marginRight: 6 }} /> Actualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div style={{ textAlign: "center", padding: 30, color: "#6b7280" }}>Cargando...</div>
        ) : permits.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: "#6b7280" }}>Aún no hay permisos emitidos.</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: 30, color: "#6b7280" }} data-testid="permits-no-results">
            No se encontraron permisos para “{search}”.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb", textAlign: "left" }}>
                  <th style={th}>Folio</th>
                  <th style={th}>Vehículo</th>
                  <th style={th}>Propietario</th>
                  <th style={th}>Expedición</th>
                  <th style={th}>Vencimiento</th>
                  <th style={th}>Estado</th>
                  {isAdmin && <th style={th}>Emitido por</th>}
                  <th style={th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const expired = isExpired(p.expires_at);
                  return (
                    <tr key={p.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={td}><b style={{ color: "#9a3412" }}>{p.folio_short}</b></td>
                      <td style={td}>{p.marca} {p.linea}<div style={{ fontSize: "0.75rem", color: "#6b7280" }}>{p.modelo} • {p.color}<br/>Serie: {p.serie}</div></td>
                      <td style={td}>{p.owner_name}</td>
                      <td style={td}>{formatDate(p.created_at)}</td>
                      <td style={td}>{formatDate(p.expires_at)}</td>
                      <td style={td}>
                        {expired ? <Badge variant="destructive">Vencido</Badge> : <Badge style={{ backgroundColor: "#10b981", color: "white" }}>Vigente</Badge>}
                      </td>
                      {isAdmin && <td style={td}>{p.created_by_name}</td>}
                      <td style={td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            onClick={() => renewPermit(p)}
                            title="Renovar (nuevo folio y fecha +30 días)"
                            data-testid={`renew-permit-btn-${p.folio_short}`}
                            disabled={renewingId === p.id}
                            style={{ ...iconBtn, borderColor: "#f58238", color: "#9a3412", cursor: renewingId === p.id ? "wait" : "pointer" }}
                          >
                            {renewingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
                          </button>
                          <button
                            onClick={() => openPrintView(p.folio_short)}
                            title="Ver e imprimir"
                            data-testid={`print-permit-btn-${p.folio_short}`}
                            style={iconBtn}
                          >
                            <Printer size={14} />
                          </button>
                          <button
                            onClick={() => downloadPdf(p.folio_short)}
                            title="Descargar PDF"
                            data-testid={`download-permit-btn-${p.folio_short}`}
                            style={iconBtn}
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => openValidation(p.folio_short, p.qr_token)}
                            title="Ver validación pública"
                            data-testid={`validate-permit-btn-${p.folio_short}`}
                            style={iconBtn}
                          >
                            <ExternalLink size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const th = { padding: "0.6rem 0.75rem", fontWeight: 600, color: "#374151", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "0.7rem 0.75rem", verticalAlign: "top", color: "#1f2937" };
const iconBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: 6,
  border: "1px solid #e5e7eb",
  backgroundColor: "white",
  color: "#374151",
  cursor: "pointer",
  textDecoration: "none",
  transition: "background-color 0.15s, border-color 0.15s",
};
